import { Component, Event, EventEmitter, Prop, State, h, type FunctionalComponent } from '@stencil/core';
import { getAPI, type UIApiConfig, type UserAPI, type UserData } from '@sydle/ui-api';
import { getConfig } from '@sydle/ui-utils';
import { type BlogCategory, type BlogConfig } from '../../../types/blog';
import { syQuerySelector } from '../../../utils/dom.utils';
import { getBaseURL, getLangPath } from '../../../utils/utils';
import { ComponentContentType } from '../sy-blog-cta-newsletter/cta-newsletter.types';

type ExtraLink = {
  text: string;
  url: string;
};

type ModalMessages = {
  successDialogTitle: string;
  successDialogMessage: string;
  alreadySubscribedDialogTitle: string;
  alreadySubscribedDialogMessage: string;
};

@Component({
  tag: 'sy-blog-header',
  styleUrl: 'scss/index.scss',
  shadow: true,
})
export class SyBlogHeader {
  /**
   * Href in logo img
   */
  @Prop() logoHref: string;

  /**
   * Text to display as headline
   */
  @Prop() headlineText: string;

  /**
   * Text to display in categories menu
   */
  @Prop() categoriesDropdownTitle: string;

  /**
   * Position of the extra links with respect to the categories
   */
  @Prop() extraLinksPosition: 'beforeCategories' | 'afterCategories' = 'afterCategories';

  /**
   * Extra links to be displayed
   */
  @Prop() extraLinks: ExtraLink[] = [];

  /**
   * Whether the newsletter button should appear
   */
  @Prop() newsletterButtonActive: boolean;

  /**
   * The newsletter component from which to take the information and to which scroll on click
   */
  @Prop() newsletterComponent: ComponentContentType;

  /**
   * Text to display in Newsletter button
   */
  @Prop() newsletterButtonText: string;

  /**
   * Text to display in Logout button
   */
  @Prop() logoutButtonText: string;

  /**
   * Event emitted when the user clicks on the newsletter button and there's no newsletter component on the DOM
   * Should open the sign in wall
   */
  @Event({ bubbles: true, composed: true }) sySignInWallOpen: EventEmitter<void>;

  @State() logoUrl = `${getBaseURL()}/blog/logo`;

  @State() newsletterCta: HTMLSyButtonElement;

  @State() user: UserData | null | undefined = getAPI<UserAPI>('userAPI')?.getUser();

  @State() categories: BlogCategory[] = [];

  @State() categoriesPromise: Promise<BlogCategory[]>;

  @State() mobileDropdownElement: HTMLSyDropdownElement;

  @State() categoriesDropdownElement: HTMLSyDropdownElement;

  @State() dropdownOpened = false;

  private loadCategories(): void {
    if (this.categoriesPromise === undefined) {
      this.categoriesPromise = window.blogFacade?.getCategories({ size: 100 }).then((response) => {
        if (response.error) {
          console.error(response.error);
        }
        this.categories = response.data as BlogCategory[];
        return response.data as BlogCategory[];
      });
    }
  }

  private getUserImageUrl(): string {
    const userId = this.user?.code;
    const userClassId = this.user?.accessToken?.payload?._class?._id;
    const userImageId = this.user?.accessToken?.payload?.photo?._id;
    const accessToken = this.user?.accessToken?.token;
    if (userId && userClassId && userImageId && accessToken) {
      return `${getBaseURL()}/api/1/${
        getConfig<UIApiConfig>('ui-api')?.APPLICATION_IDENTIFIER || ''
      }/_classId/${userClassId}/_download/${userId}/${userImageId}?accessToken=${accessToken}`;
    } else {
      return '';
    }
  }

  private handleCategoryDropdownOpen(): void {
    this.dropdownOpened = true;
    this.loadCategories();
  }

  private handleCategoryDropdownClose(): void {
    this.dropdownOpened = false;
  }

  private handleNewsletterButtonClick(newsletterComponent: ComponentContentType | undefined): void {
    if (newsletterComponent) {
      const newsletterElement = syQuerySelector(`[componentid="${newsletterComponent._id}"]`);
      if (newsletterElement) {
        newsletterElement.scrollIntoView({ behavior: 'smooth' });
      } else {
        const newslettersToSubscribe = getConfig<BlogConfig>('sydle-ui-blog')?.consentListsByLanguage?.map(
          (consent) => {
            return { _id: consent._id };
          },
        );

        if (this.user) {
          const messages = {
            successDialogTitle: this.newsletterComponent.settings.successDialogTitle,
            successDialogMessage: this.newsletterComponent.settings.successDialogMessage,
            alreadySubscribedDialogTitle: this.newsletterComponent.settings.alreadySubscribedDialogTitle,
            alreadySubscribedDialogMessage: this.newsletterComponent.settings.alreadySubscribedDialogMessage,
          };
          this.subscribeToNewsletter(newslettersToSubscribe || [], messages);
        } else {
          sessionStorage.setItem('newslettersToSubscribeHeader', JSON.stringify(newslettersToSubscribe));
          this.sySignInWallOpen.emit();
        }
      }
      this.newsletterCta.blur();
    } else {
      console.error('No newsletter component found');
    }
  }

  private renderCategories = (): FunctionalComponent => {
    return this.categories.length ? (
      this.categories.map((category) => (
        <a
          href={category.link}
          key={category._id}
          onClick={() => {
            this.categoriesDropdownElement.close();
            this.mobileDropdownElement.close();
          }}
        >
          <sy-text>{category.name}</sy-text>
        </a>
      ))
    ) : (
      <div id="skeleton-container">
        <sy-skeleton count="4" />
      </div>
    );
  };

  private renderExtraLinks = (): FunctionalComponent[] => {
    return this.extraLinks.map((link) => (
      <a href={link.url} key={link.url} class="extra-link" onClick={() => this.mobileDropdownElement.close()}>
        <sy-text>{link.text}</sy-text>
      </a>
    ));
  };

  private renderUserIcon = (): FunctionalComponent => {
    return (
      <sy-avatar
        small
        rounded
        class="user-avatar"
        image={this.getUserImageUrl()}
        initials={this?.user?.name?.toUpperCase()[0] || ''}
        initialsSize={1}
      />
    );
  };

  private subscribeToNewsletter(items: string[], messages: ModalMessages): void {
    const modal = syQuerySelector('sy-blog-alert-modal') as HTMLSyBlogAlertModalElement;
    window.blogFacade
      .registerUserConsents({ consents: items })
      .then((_output) => _output.data?.newSubscriptions)
      .then((newSubscriptions) => {
        if (newSubscriptions && newSubscriptions.length > 0) {
          modal.dialogTitle = messages.successDialogTitle;
          modal.dialogMessage = messages.successDialogMessage;
        } else {
          modal.dialogTitle = messages.alreadySubscribedDialogTitle;
          modal.dialogMessage = messages.alreadySubscribedDialogMessage;
        }
        modal.open();
      });
  }

  componentDidLoad() {
    if (this.user) {
      const newslettersToSubscribe =
        sessionStorage.getItem('newslettersToSubscribeHeader') &&
        JSON.parse(sessionStorage.getItem('newslettersToSubscribeHeader') || '');
      if (newslettersToSubscribe && this.newsletterComponent) {
        const messages = {
          successDialogTitle: this.newsletterComponent.settings.successDialogTitle,
          successDialogMessage: this.newsletterComponent.settings.successDialogMessage,
          alreadySubscribedDialogTitle: this.newsletterComponent.settings.alreadySubscribedDialogTitle,
          alreadySubscribedDialogMessage: this.newsletterComponent.settings.alreadySubscribedDialogMessage,
        };
        this.subscribeToNewsletter(newslettersToSubscribe, messages);
        sessionStorage.removeItem('newslettersToSubscribeHeader');
      }
    }
  }

  render() {
    return (
      <nav>
        <sy-blog-alert-modal />
        <header>
          <div id="left" class="header-section">
            <div id="mobile-menu" class="categories">
              <a href="#" onClick={(e) => e.preventDefault()}>
                <sy-icon name="menu" size="24" />
              </a>
              <sy-dropdown
                onSyDropdownDidOpen={() => this.handleCategoryDropdownOpen()}
                onSyDropdownDidClose={() => this.handleCategoryDropdownClose()}
                toggle-by="#mobile-menu"
                offset-distance="0"
                id="mobile-menu-dropdown"
                ref={(el) => (this.mobileDropdownElement = el)}
              >
                <div class="user-mobile">
                  <a href={`${getLangPath()}/blog/`} onClick={() => this.mobileDropdownElement.close()}>
                    <sy-text>{this.headlineText}</sy-text>
                  </a>
                  {this.user ? (
                    <span id="user-info">
                      {this.renderUserIcon()}
                      <sy-text type="small">{this.user.name}</sy-text>
                      <sy-button
                        icon="logout-1"
                        content="icon-only"
                        variant="filled"
                        color="secondary"
                        onClick={() => {
                          getAPI<UserAPI>('userAPI')?.logout();
                          window.location.reload();
                        }}
                      >
                        <sy-text type="small">{'Log out'}</sy-text>
                      </sy-button>
                    </span>
                  ) : null}
                </div>
                {this.extraLinks?.length > 0 && this.extraLinksPosition === 'beforeCategories' ? (
                  <div class="extra-links-container">
                    {this.renderExtraLinks()}
                    <hr />
                  </div>
                ) : null}
                <div class="category-container">{this.renderCategories()}</div>
                {this.extraLinks?.length > 0 && this.extraLinksPosition === 'afterCategories' ? (
                  <div class="extra-links-container">
                    <hr />
                    {this.renderExtraLinks()}
                  </div>
                ) : null}
              </sy-dropdown>
            </div>
            <a href={this.logoHref}>
              <img id="logo" src={this.logoUrl} aria-label="Site" alt="Logo" />
            </a>
            <a id="headline" href={`${getLangPath()}/blog/`}>
              <sy-text>{this.headlineText}</sy-text>
            </a>
            {this.extraLinks?.length > 0 && this.extraLinksPosition === 'beforeCategories'
              ? this.renderExtraLinks()
              : null}
            <div id="categories-desktop" class="categories">
              <a href="#" onClick={(e) => e.preventDefault()}>
                <sy-text>{this.categoriesDropdownTitle}</sy-text>
                <sy-icon
                  style={{ display: this.dropdownOpened ? 'none' : 'inline-block' }}
                  name="keyboard_arrow_down"
                  size="8"
                />
                <sy-icon
                  style={{ display: this.dropdownOpened ? 'inline-block' : 'none' }}
                  name="keyboard_arrow_up"
                  size="8"
                />
              </a>
              <sy-dropdown
                onSyDropdownDidOpen={() => this.handleCategoryDropdownOpen()}
                onSyDropdownDidClose={() => this.handleCategoryDropdownClose()}
                toggle-by="#categories-desktop"
                offset-distance="0"
                key="dropdown"
                ref={(el) => (this.categoriesDropdownElement = el)}
              >
                <div class="category-container">{this.renderCategories()}</div>
              </sy-dropdown>
            </div>

            {this.extraLinks?.length > 0 && this.extraLinksPosition === 'afterCategories'
              ? this.renderExtraLinks()
              : null}
          </div>

          <div id="center" class="header-section"></div>

          <div id="right" class="header-section">
            {getConfig<BlogConfig>('sydle-ui-blog')?.enableNewsletter && this.newsletterButtonActive ? (
              <sy-button
                ref={(el: HTMLSyButtonElement) => (this.newsletterCta = el)}
                id="cta"
                icon="email"
                content="icon-right"
                variant="filled"
                color="secondary"
                onClick={() => this.handleNewsletterButtonClick(this.newsletterComponent)}
                target="_self"
              >
                {this.newsletterButtonText}
              </sy-button>
            ) : null}
            {this.user && (
              <div class="user" id="user-desktop">
                {this.user
                  ? [
                      this.renderUserIcon(),
                      <sy-dropdown toggle-by="#user-desktop" offset-distance="0" key="dropdown">
                        <div class="user-menu">
                          <a class="username">
                            <sy-text type="small">{this.user.name}</sy-text>
                          </a>
                          <sy-divider></sy-divider>
                          <a
                            class="logout"
                            onClick={() => {
                              getAPI<UserAPI>('userAPI')?.logout();
                              window.location.reload();
                            }}
                          >
                            <sy-text type="bold">{this.logoutButtonText}</sy-text>
                          </a>
                        </div>
                      </sy-dropdown>,
                    ]
                  : null}
              </div>
            )}
            <a id="search" href={`${getLangPath()}/blog/search`}>
              <sy-icon class="search" name="search" size="24"></sy-icon>
            </a>

            {(getConfig<BlogConfig>('sydle-ui-blog')?.configByLanguage || []).length > 1 && (
              <div class="language-buttons-header" id="supported-languages-desktop" style={{ paddingRight: '0' }}>
                <sy-blog-language-buttons />
              </div>
            )}
          </div>
        </header>
      </nav>
    );
  }
}
